import React, { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import {
  FaExternalLinkAlt,
  FaSearch,
  FaCode,
  FaCalendarAlt,
  FaArrowDown,
  FaArrowUp,
} from "react-icons/fa";

type Category = "all" | "fullstack" | "financial" | "website" | "data science";

interface Project {
  title: string;
  description: string;
  image: string;
  technologies: string[];
  live: string;
  category: Category;
  status: "live" | "development";
  featured?: boolean;
  date: string;
}

function Icon(component: any, className?: string) {
  return React.createElement(component as React.ComponentType<{ className?: string }>, { className });
}

const categories = [
  { id: "all", label: "All" },
  { id: "fullstack", label: "Full Stack" },
  { id: "financial", label: "Financial" },
  { id: "website", label: "Websites" },
  { id: "data science", label: "Data Science" },
];

const projects: Project[] = [
  {
    title: "Computer Support Hub",
    description: "Website dents, teachers, and anyone who needs digital skills, computer repair, software installation, and ICT support — whether on-site or remotely across Rwanda. Founded by students who saw the challenges many face with computers and ICT, we are here for you wherever you ",
    image: "./images/project77.png",
    technologies: ["react","Tailwind CSS", "Node.js", "MongoDB"],
    live: "https://computer-support-ict.vercel.app/",
    category: "website",
    status: "live",
    featured: true,
    date: "2025-08",
  },
  {
    title: "AI-Powered recruitment platform",
    description: "An AI-driven recruitment platform that streamlines candidate sourcing, screening, and matching for HR professionals. using AI algorithms to analyze resumes, assess skills, and predict candidate-job fit.",
    image: "./images/project66.png", 
    technologies: ["TypeScript", "Tailwind CSS", "Node.js", "MongoDB", "Gemini API", "SOCKET.io"],
    live: "https://umuravarecruit-system.vercel.app",
    category: "data science",
    status: "live",
    featured: true,
    date: "2025-12",
  },
  {
    title: "University of Rwanda Assistant Chatbot system",
    description: "AI-powered fullstack  assistant system for students and staff, providing instant answers to academic and administrative queries.",
    image: "./images/project55.png", 
    technologies: ["TypeScript", "Shadcn UI", "Node.js", "MongoDB", "OpenAi API"],
    live: "#",
    category: "fullstack",
    status: "live",
    featured: true,
    date: "2026-03",
  },
  
  {
    title: "CitizenHub",
    description: "Full-stack citizen complaint management system with real-time updates and admin dashboards.",
    image: "./images/project1.png",
    technologies: ["React", "TypeScript", "MaterialUI", "MongoDB", "Node.js"],
    live: "https://citizenhub-project.vercel.app",
    category: "fullstack",
    status: "live",
    featured: true,
    date: "2025-11",
  },
  {
    title: "Umuganda Tracking System",
    description: "Digital platform for managing Umuganda attendance, fines automation, analytics and notifications.",
    image: "./images/project3.png",
    technologies: ["React", "Node.js", "MaterialUI", "MongoDB", "Chart.js"],
    live: "https://umuganda-tracking-system.vercel.app",
    category: "fullstack",
    status: "live",
    featured: true,
    date: "2025-07",
  },
  {
    title: "E-Commerce Web Application",
    description: "Online shopping platform with product management, checkout flows, and Stripe payment integration.",
    image: "./images/project33.png",
    technologies: ["React", "Node.js", "Tailwind CSS", "MongoDB", "Stripe API"],
    live: "https://evodeshop.vercel.app",
    category: "financial",
    status: "live",
    date: "2026-06",
  },
  {
    title: "DYPSE — Youth Profiling System",
    description: "Youth skill profiling and employment mapping platform using ML models for talent-to-job matching.",
    image: "./images/project444.png",
    technologies: ["TypeScript", "Node.js", "Python", "ML", "MongoDB", "Plotly.js"],
    live: "https://dypse.vercel.app",
    category: "fullstack",
    status: "live",
    featured: true,
    date: "2025-09",
  },
  {
    title: "LearnWithEvode",
    description: "Interactive education platform offering basic tech skills training with hands-on projects.",
    image: "./images/project22.png",
    technologies: ["HTML5", "CSS3", "JavaScript"],
    live: "https://codewithevode.vercel.app",
    category: "website",
    status: "development",
    date: "2024-09",
  },
  {
    title: "City of Kigali | IKAZE",
    description: "Smart entry and service management with IoT plate scanning, parking tracking and service dashboards.",
    image: "./images/project8.png",
    technologies: ["TypeScript", "Node.js", "Express", "MongoDB", "Socket.io", "IoT"],
    live: "https://uat-ikaze.kigalicity.gov.rw/login",
    category: "fullstack",
    status: "live",
    featured: true,
    date: "2026-02",
  },
  {
    title: "E-VURE Dermasight AI",
    description: "AI-powered dermatology assistant detecting 22 skin diseases from images with treatment recommendations.",
    image: "./images/project7.png",
    technologies: ["TypeScript", "Python", "TensorFlow", "Scikit-learn", "MongoDB"],
    live: "https://evure-derma-sights.onrender.com/",
    category: "data science",
    status: "live",
    date: "2026-03",
  },
  {
    title: "Imirire Yacu — Ending Hidden Hunger",
    description: "Data science project analyzing food security trends with Python for data manipulation and visualization.",
    image: "./images/project6.png",
    technologies: ["React", "Node.js", "Python", "Pandas", "Matplotlib", "Seaborn"],
    live: "#",
    category: "data science",
    status: "development",
    date: "2024-07",
  },
  {
    title: "AURA-ATELIER | tatoes inking and EyelASH Studio",
    description: " Dynamic website for a high-fashion minimalist studio dedicated to precision cashmere lash extensions, keratin lifts, and sterile fine-line single-needle tattoos in Kiyovu.",
    image: "./images/project10.png",
    technologies: ["React", "Node.js", "express.js", "MongoDB"],
    live: "https://aura-studio-frontend.vercel.app/",
    category: "website",
    status: "live",
    date: "2025-01",
  },
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.06, delayChildren: 0.1 } },
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: { y: 0, opacity: 1, transition: { duration: 0.45, ease: [0.25, 0.46, 0.45, 0.94] } },
};

const ITEMS_PER_PAGE = 6;

const Projects: React.FC = () => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [category, setCategory] = useState<Category>("all");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState<"newest" | "oldest">("newest");

  const filtered = useMemo(() => {
    let data = [...projects];
    if (category !== "all") data = data.filter((p) => p.category === category);
    if (search) {
      const q = search.toLowerCase();
      data = data.filter(
        (p) =>
          p.title.toLowerCase().includes(q) ||
          p.description.toLowerCase().includes(q) ||
          p.technologies.some((t) => t.toLowerCase().includes(q))
      );
    }
    data.sort((a, b) => sort === "newest"
      ? b.date.localeCompare(a.date)
      : a.date.localeCompare(b.date)
    );
    return data;
  }, [category, search, sort]);

  const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE);
  const paginated = filtered.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE);

  const handleCategoryChange = (c: Category) => { setCategory(c); setPage(1); };
  const handleSearchChange = (v: string) => { setSearch(v); setPage(1); };

  return (
    <section id="projects" className="py-14 md:py-20 bg-gradient-to-b from-tertiary/30 to-primary relative overflow-hidden">
      {/* Subtle grid */}
      <div
        className="absolute inset-0 opacity-[0.03] pointer-events-none"
        style={{
          backgroundImage: "linear-gradient(var(--color-accent) 1px, transparent 1px), linear-gradient(90deg, var(--color-accent) 1px, transparent 1px)",
          backgroundSize: "48px 48px",
        }}
      />

      <div className="container mx-auto px-4 sm:px-6 relative z-10">
        <motion.div ref={ref} variants={containerVariants} initial="hidden" animate={inView ? "visible" : "hidden"} className="max-w-6xl mx-auto">

          {/* Header */}
          <motion.div variants={itemVariants} className="text-center mb-10">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md border border-border/50 mb-5">
              {Icon(FaCode, "text-xs text-secondary")}
              <span className="font-mono text-[11px] text-textSecondary/60 tracking-wider uppercase">Portfolio</span>
            </div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-textPrimary">
              Featured{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-500 to-purple-500">Projects</span>
            </h2>
            <p className="text-textSecondary text-sm mt-3 max-w-md mx-auto">
              Transforming ideas into impactful digital solutions
            </p>
          </motion.div>

          {/* Filters */}
          <motion.div variants={itemVariants} className="flex flex-col sm:flex-row items-center justify-between gap-4 mb-10 max-w-4xl mx-auto">
            {/* Category pills */}
            <div className="flex flex-wrap justify-center gap-2">
              {categories.map((c) => (
                <button
                  key={c.id}
                  onClick={() => handleCategoryChange(c.id as Category)}
                  className={`px-4 py-2 rounded-lg text-xs font-semibold transition-all duration-300 ${
                    category === c.id
                      ? "bg-gradient-to-r from-blue-500 to-purple-500 text-white shadow-lg"
                      : "bg-tertiary/50 border border-border/50 text-textSecondary hover:text-textPrimary hover:border-secondary/50"
                  }`}
                >
                  {c.label}
                </button>
              ))}
            </div>

            {/* Search + Sort */}
            <div className="flex items-center gap-2 w-full sm:w-auto">
              <div className="relative flex-1 sm:w-64">
                <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white dark:bg-[#161b22] border border-border/50">
                  {Icon(FaSearch, "text-xs text-textSecondary/50")}
                  <input
                    type="text"
                    placeholder="Search projects..."
                    value={search}
                    onChange={(e) => handleSearchChange(e.target.value)}
                    className="flex-1 bg-transparent text-textPrimary text-sm placeholder:text-textSecondary/40 outline-none"
                  />
                </div>
              </div>
              <button
                onClick={() => setSort((s) => s === "newest" ? "oldest" : "newest")}
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white dark:bg-[#161b22] border border-border/50 text-textSecondary hover:text-textPrimary hover:border-secondary/50 transition-all duration-300"
                title={`Sort by ${sort === "newest" ? "oldest" : "newest"}`}
              >
                {Icon(sort === "newest" ? FaArrowDown : FaArrowUp, "text-[10px]")}
                <span className="text-[10px] font-semibold hidden sm:inline">{sort === "newest" ? "Newest" : "Oldest"}</span>
              </button>
            </div>
          </motion.div>

          {/* Project Grid */}
          <motion.div variants={containerVariants} className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {paginated.map((project) => (
              <motion.div
                key={project.title}
                variants={itemVariants}
                whileHover={{ y: -4 }}
                className="group relative rounded-xl bg-white dark:bg-[#161b22] border border-border/40 overflow-hidden transition-all duration-300 hover:border-secondary/40 hover:shadow-lg hover:shadow-secondary/5"
              >
                {/* Image — clickable */}
                <a
                  href={project.live}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block relative h-44 overflow-hidden bg-[#f0f0f0] dark:bg-[#0d1117]"
                >
                  <img
                    src={project.image}
                    alt={project.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105 cursor-pointer"
                  />
                  {/* Status badge */}
                  <div className={`absolute top-3 right-3 px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider pointer-events-none ${
                    project.status === "live" ? "bg-green-500 text-white" : "bg-yellow-500 text-white"
                  }`}>
                    {project.status === "live" ? "Live" : "Dev"}
                  </div>
                  {/* Featured badge */}
                  {project.featured && (
                    <div className="absolute top-3 left-3 px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-secondary text-white pointer-events-none">
                      Featured
                    </div>
                  )}
                </a>

                {/* Content */}
                <div className="p-5">
                  <div className="flex items-center justify-between mb-1.5">
                    <h3 className="text-base font-bold text-textPrimary line-clamp-1 group-hover:text-secondary transition-colors duration-300">
                      {project.title}
                    </h3>
                    <span className="flex items-center gap-1 text-[10px] text-textSecondary/50 font-mono flex-shrink-0 ml-2">
                      {Icon(FaCalendarAlt, "text-[9px]")}
                      {project.date}
                    </span>
                  </div>
                  <p className="text-xs text-textSecondary leading-relaxed mb-3 line-clamp-2">
                    {project.description}
                  </p>

                  {/* Tech tags */}
                  <div className="flex flex-wrap gap-1.5 mb-4">
                    {project.technologies.slice(0, 5).map((t) => (
                      <span
                        key={t}
                        className="px-2 py-0.5 text-[10px] font-mono rounded border border-border/40 text-textSecondary/70"
                      >
                        {t}
                      </span>
                    ))}
                    {project.technologies.length > 5 && (
                      <span className="px-2 py-0.5 text-[10px] font-mono rounded border border-border/40 text-textSecondary/50">
                        +{project.technologies.length - 5}
                      </span>
                    )}
                  </div>

                  {/* View Live button — always visible */}
                  <a
                    href={project.live}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-gradient-to-r from-blue-500 to-purple-500 text-white text-xs font-semibold hover:shadow-lg hover:shadow-secondary/20 transition-all duration-300"
                  >
                    {Icon(FaExternalLinkAlt, "text-[10px]")}
                    View Live
                  </a>
                </div>
              </motion.div>
            ))}
          </motion.div>

          {/* Empty State */}
          {filtered.length === 0 && (
            <div className="text-center py-16">
              <p className="text-textSecondary text-sm">No projects found. Try adjusting your search or filter.</p>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-10">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-3 py-1.5 rounded-lg text-xs font-semibold border border-border/50 text-textSecondary hover:text-textPrimary hover:border-secondary/50 disabled:opacity-30 disabled:cursor-not-allowed transition-all duration-300"
              >
                Prev
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                <button
                  key={p}
                  onClick={() => setPage(p)}
                  className={`w-8 h-8 rounded-lg text-xs font-semibold transition-all duration-300 ${
                    page === p
                      ? "bg-gradient-to-r from-blue-500 to-purple-500 text-white shadow-lg"
                      : "border border-border/50 text-textSecondary hover:text-textPrimary hover:border-secondary/50"
                  }`}
                >
                  {p}
                </button>
              ))}
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="px-3 py-1.5 rounded-lg text-xs font-semibold border border-border/50 text-textSecondary hover:text-textPrimary hover:border-secondary/50 disabled:opacity-30 disabled:cursor-not-allowed transition-all duration-300"
              >
                Next
              </button>
            </div>
          )}
        </motion.div>
      </div>
    </section>
  );
};

export default Projects;
